import React, { useEffect, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { FocusTrap } from './primitives/FocusTrap';
import { Button, IconButton } from './Button';
import { Icon } from './Icon';
import { zIndex as Z } from '../../tokens/zIndex';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { dialogEntrance } from './primitives/motion';
import styles from './Modal.module.css';

const modalOverlay = cva(styles.overlay, {
  variants: {
    centered: { true: styles.centered },
  },
});

type ModalOverlayVariants = VariantProps<typeof modalOverlay>;

interface ModalProps extends ModalOverlayVariants {
  /** Whether the modal is visible */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Title text or element */
  title?: React.ReactNode;
  /** Footer content — set to null to hide */
  footer?: React.ReactNode | null;
  /** Modal body width */
  width?: number | string;
  /** Vertically center the modal */
  centered?: boolean;
  /** Show the X close button */
  closable?: boolean;
  /** Close when clicking the overlay mask */
  maskClosable?: boolean;
  /** Destroy children when closed instead of hiding */
  destroyOnClose?: boolean;
  /** Custom z-index */
  zIndex?: number;
  /** Show loading spinner on the OK button */
  confirmLoading?: boolean;
  /** OK button text */
  okText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Called when OK is clicked */
  onOk?: () => void;
  /** OK button variant */
  okVariant?: 'default' | 'primary' | 'text' | 'link';
  /** OK button as danger */
  okDanger?: boolean;
  /** Modal body content */
  children?: React.ReactNode;
  /** Extra body styles (user-provided passthrough) */
  bodyStyle?: React.CSSProperties;
  /** Extra styles applied to the modal box (outer container, not the body). */
  style?: React.CSSProperties;
  /** Override the aria-labelledby id on the dialog element. Use when the caller
   * manages the title element externally (e.g. SettingsShell renders its own
   * title bar and passes title={null} to suppress the built-in header). */
  'aria-labelledby'?: string;
}

/**
 * Modal — dialog overlay with title, body, and footer.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | open | boolean | — | Visibility |
 * | onClose | () => void | — | Close handler |
 * | title | ReactNode | — | Header title |
 * | footer | ReactNode \| null | default OK/Cancel | Footer content (null hides) |
 * | width | number \| string | 480 | Modal width |
 * | centered | boolean | false | Vertically center |
 * | closable | boolean | true | Show X button |
 * | maskClosable | boolean | true | Close on mask click |
 * | destroyOnClose | boolean | false | Unmount children on close |
 * | zIndex | number | Z.modal | z-index layer |
 * | confirmLoading | boolean | false | Loading state on OK button |
 * | okText | string | 'OK' | OK button label |
 * | cancelText | string | 'Cancel' | Cancel button label |
 * | onOk | () => void | — | OK button handler |
 * | okVariant | string | 'primary' | OK button variant |
 * | okDanger | boolean | false | OK button danger style |
 * | bodyStyle | CSSProperties | — | Extra body styles |
 *
 * @example
 * <Modal open={isOpen} onClose={close} title="Confirm">
 *   Are you sure you want to proceed?
 * </Modal>
 *
 * <Modal open={isOpen} onClose={close} title="Delete" okDanger onOk={handleDelete}>
 *   This action cannot be undone.
 * </Modal>
 */
export function Modal({
  open,
  onClose,
  title,
  footer,
  width = 480,
  centered = false,
  closable = true,
  maskClosable = true,
  destroyOnClose: _destroyOnClose = false,
  zIndex = Z.modal,
  confirmLoading = false,
  okText = 'OK',
  cancelText = 'Cancel',
  onOk,
  okVariant = 'primary',
  okDanger = false,
  children,
  bodyStyle,
  style,
  'aria-labelledby': ariaLabelledby,
}: ModalProps) {
  const titleId = useId();
  const shouldReduce = useReducedMotion();

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleMaskClick = () => {
    if (maskClosable) onClose();
  };

  const defaultFooter =
    footer === undefined ? (
      <>
        <Button variant="text" onClick={onClose}>{cancelText}</Button>
        <Button variant={okVariant} danger={okDanger} loading={confirmLoading} onClick={onOk ?? onClose}>
          {okText}
        </Button>
      </>
    ) : (
      footer
    );

  const panelInitial = shouldReduce ? { opacity: 0 } : dialogEntrance.initial;
  const panelAnimate = shouldReduce ? { opacity: 1 } : dialogEntrance.animate;
  const panelExit = shouldReduce ? { opacity: 0 } : dialogEntrance.exit;
  const panelTransition = shouldReduce ? { duration: 0 } : dialogEntrance.transition;

  // Runtime values: zIndex and width are passed via CSS custom properties
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width;

  return (
    <AnimatePresence>
      {open && (
        <Portal>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: duration.slow / 1000 }}
            className={clsx(modalOverlay({ centered: centered || undefined }))}
            style={{ '--modal-z': zIndex } as React.CSSProperties}
            onClick={(e) => { if (e.target === e.currentTarget) handleMaskClick(); }}
          >
            <FocusTrap active={open} onEscape={onClose} aria-labelledby={ariaLabelledby ?? (title ? titleId : undefined)}>
              <motion.div
                initial={panelInitial}
                animate={panelAnimate}
                exit={panelExit}
                transition={panelTransition}
                className={styles.box}
                style={{ '--modal-width': resolvedWidth, ...style } as React.CSSProperties}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                {(title || closable) && (
                  <div className={styles.header}>
                    {title ? <div id={titleId} className={styles.title}>{title}</div> : <div />}
                    {closable && (
                      <IconButton
                        size="sm"
                        pill
                        hoverBackground="var(--surface-container-high)"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                      >
                        <Icon name="close" size={16} />
                      </IconButton>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className={styles.body} style={bodyStyle}>{children}</div>

                {/* Footer */}
                {defaultFooter !== null && <div className={styles.footer}>{defaultFooter}</div>}
              </motion.div>
            </FocusTrap>
          </motion.div>
        </Portal>
      )}
    </AnimatePresence>
  );
}
